import React, { useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import "./FuelAnalysis.css";
import ApiManager from "../../ApiManager/ApiManager";
import Select from "react-select";
import PERMISSIONS from "../../permissions";
import Table from "../../components/Table/Table";
import { hasPermission } from "../../components/RoleBasedComponents";
import { useAuth } from "../../components/AuthProvider";
import EditFuelTransaction from "../Fuel/EditFuelTransaction";
import ViewEntity from "../../components/ViewEntity/ViewEntity";
import Modal from "../../components/Modal/Modal";

function FuelAnalysis() {
  const location = useLocation();
  const { userPermissions } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const carId = queryParams.get("carId");

  // API-related states
  const [fuelData, setFuelData] = React.useState([]);
  const [conversionRates, setConversionRates] = React.useState({});

  // Sidebar states
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarData, setSidebarData] = useState([]);
  const [rangeLabel, setRangeLabel] = useState("");

  // UI states for chart configuration
  const [selectedFuelType, setSelectedFuelType] = React.useState("PETROL");
  const [transactionGap, setTransactionGap] = React.useState(5);
  const [transactionGapPie, setTransactionGapPie] = React.useState(5);
  const [hasMorePages, setHasMorePages] = useState(true);

  const [rowId, setRowId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDetails, setDetails] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [editRowId, setEditRowId] = useState(null);

  const [barData, setBarData] = React.useState([]);
  const [pieData, setPieData] = React.useState([]);

  // Pagination states
  const [page, setPage] = React.useState(1);
  const pageSize = 100;
  const [loadingMore, setLoadingMore] = React.useState(false);

  const [carList, setCarList] = useState([]);
  // const [selectedCarId, setSelectedCarId] = useState(carId); // Initially from URL
  const [selectedCarInfo, setSelectedCarInfo] = useState(null);
  // Add sinceTime state: default to one month before now (in seconds)
  const [sinceTime, setSinceTime] = useState(() => {
    const now = new Date();
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1));
    return Math.floor(oneMonthAgo.getTime() / 1000);
  });

  // Extract unique fuel types for the dropdown
  const fuelTypes =
    Array.isArray(fuelData) && fuelData.length
      ? [...new Set(fuelData.map((item) => item.fuelType))]
      : [];

  const transactionGapOptions = useMemo(() => {
    const count = Array.isArray(fuelData) ? fuelData.length : 0;
    if (count <= 1) return [1]; // Fallback for low data count
    if (count < 5) return [count]; // Small dataset fallback
    return Array.from({ length: count - 5 + 1 }, (_, i) => i + 5);
  }, [fuelData]);

  React.useEffect(() => {
    const fetchCars = async () => {
      try {
        const params = new URLSearchParams({
          page_size: 999999999,
          page: 1,
        });
        const response = await ApiManager.getAllCars(params);
        if (!response.ok) throw new Error("Failed to fetch cars");
        const cars = await response.json();
        setCarList(cars || []);

        // If no carId from URL, use first car
        if (!carId && cars.length > 0) {
          // setSelectedCarId(cars[0].id);
          setSelectedCarInfo(cars[0]);
        } else {
          const matchedCar = cars.find((c) => c.id == carId);
          setSelectedCarInfo(matchedCar);
        }
      } catch (error) {
        console.error("Error fetching cars:", error);
      }
    };

    fetchCars();
  }, []);

  const fetchFuelAnalysis = async (currentPage = 1) => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        page_size: pageSize,
        carId: selectedCarInfo?.id,
        from: sinceTime, // Add 'from' param
      });

      const response = await ApiManager.getFuelAnalysis(params);
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      // Defensive: treat anything falsy or non-array as []
      const safeFuelData = Array.isArray(data.fuelData) ? data.fuelData : [];
      setHasMorePages(Array.isArray(safeFuelData) && safeFuelData.length === pageSize);
      // Normalize conversionRates keys
      const normalizedRates = {};
      for (const key in data.conversionRates) {
        normalizedRates[key.toUpperCase()] = {};
        for (const innerKey in data.conversionRates[key]) {
          normalizedRates[key.toUpperCase()][innerKey.toUpperCase()] =
            data.conversionRates[key][innerKey];
        }
      }

      setConversionRates(normalizedRates);

      if (currentPage === 1) {
        // First page: replace data
        setFuelData(safeFuelData);
        if (!Array.isArray(safeFuelData) || safeFuelData.length === 0) {
          setBarData([]);
          setPieData([]);
        }
        // Update selectedFuelType if necessary
        const uniqueTypes = [
          ...new Set(safeFuelData.map((item) => item.fuelType)),
        ];
        if (!uniqueTypes.includes(selectedFuelType)) {
          setSelectedFuelType(uniqueTypes[0] || "PETROL");
        }
      } else {
        // Subsequent pages: append new data
        setFuelData((prev) => [
          ...prev,
          ...safeFuelData
        ]);
      }
    } catch (error) {
      // On error, clear data
      setFuelData([]);
      setBarData([]);
      setPieData([]);
      console.error("Error fetching data:", error);
    }
  };

  // Fetch data from API on mount and when selectedCarInfo or sinceTime changes
  React.useEffect(() => {
    if (selectedCarInfo) {
      fetchFuelAnalysis(1);
    } else {
      // If no car selected, clear data
      setFuelData([]);
      setBarData([]);
      setPieData([]);
    }
  }, [selectedCarInfo, sinceTime]);

  // Update barData when fuelData or related states change
  React.useEffect(() => {
    if (!Array.isArray(fuelData) || fuelData.length === 0) {
      setBarData([]);
      return;
    }
    const sorted = fuelData;
    const groups = [];

    for (let i = 0; i < sorted.length; i += transactionGap) {
      const block = sorted.slice(i, i + transactionGap);
      if (i === 0 && block.length < 2) {
        groups.push({
          name: `${i + 1}-${i + block.length}`,
          value: 0,
          totalAmount: parseFloat(
            block.reduce((sum, tx) => sum + tx.fuelAmount, 0).toFixed(2)
          ),
        });
        continue;
      }

      const startReading =
        i === 0 ? block[0].meterReading : sorted[i - 1].meterReading;
      const endReading = block[block.length - 1].meterReading;
      const distance = startReading - endReading;

      // Determine fuel index range
      const isFirstBlock = i === 0;
      const fuelStartIdx = isFirstBlock ? i + 1 : i;
      const fuelEndIdx = i + transactionGap - 1;

      let totalFuelVolume = 0;
      let totalAmount = 0;
      for (let j = fuelStartIdx; j <= fuelEndIdx && j < sorted.length; j++) {
        const tx = sorted[j];
        const rate = conversionRates[selectedFuelType]?.[tx.fuelType] ?? 1;
        totalFuelVolume += tx.fuelVolume * rate;
        totalAmount += tx.fuelAmount;
      }

      const mileage = totalFuelVolume > 0 ? distance / totalFuelVolume : 0;
      groups.push({
        name: `${i + 1}-${i + block.length}`,
        value: parseFloat(mileage.toFixed(2)),
        totalAmount: parseFloat(totalAmount.toFixed(2)),
      });
    }

    setBarData(groups);
  }, [fuelData, conversionRates, selectedFuelType, transactionGap]);

  React.useEffect(() => {
    if (!Array.isArray(fuelData) || fuelData.length === 0) {
      setPieData([]);
      return;
    }
    const selectedTransactions = fuelData.slice(0, transactionGapPie);
    const groupTotals = {};
    selectedTransactions.forEach((transaction) => {
      const type = transaction.fuelType;
      groupTotals[type] = (groupTotals[type] || 0) + transaction.fuelAmount;
    });
    const data = Object.entries(groupTotals).map(([type, totalAmount]) => ({
      name: `${type} (₹${totalAmount.toFixed(2)})`,
      value: parseFloat(totalAmount.toFixed(2)),
    }));
    setPieData(data);
  }, [transactionGapPie, fuelData]);

  // Handler for fuel type dropdown
  const handleFuelTypeChange = (e) => {
    setSelectedFuelType(e.target.value);
    // Optionally, reset pagination if fuel type changes
    setPage(1);
  };

  // Handler for transaction gap dropdown
  const handleTransactionGapChange = (e) => {
    setTransactionGap(parseInt(e.target.value, 10));
  };

  const handleTransactionGapPieChange = (e) => {
    setTransactionGapPie(parseInt(e.target.value, 10));
  };

  // Function to load more data
  const loadMoreData = async () => {
    if (loadingMore) return;
    if (!hasMorePages) return;
    console.log("load more");
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const params = new URLSearchParams({
        page: nextPage,
        page_size: pageSize,
        carId: carId,
      });
      const response = await ApiManager.getFuelAnalysis(params);
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      setHasMorePages(Array.isArray(data.fuelData) && data.fuelData.length === pageSize);
      // Check if data.fuelData is an array before appending
      if (Array.isArray(data.fuelData)) {
        setFuelData((prev) => [...prev, ...data.fuelData]);
        setPage(nextPage);
      } else {
        console.log(
          "No more fuel data available or unexpected response format."
        );
      }
    } catch (error) {
      console.error("Error loading more data:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const prevScrollLeft = useRef(0);
  const threshold = 50;

  const handleScroll = (e) => {
    const { scrollLeft, clientWidth, scrollWidth } = e.currentTarget;
    // Only check for pagination if the user is scrolling right
    if (scrollLeft > prevScrollLeft.current) {
      if (scrollLeft + clientWidth >= scrollWidth - threshold) {
        loadMoreData();
      }
    }
    prevScrollLeft.current = scrollLeft;
  };

  // Dummy pie chart data and colors (kept from your original code)
  const COLORS = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff8042",
    "#ffbb28",
    "#00C49F",
  ];

  const table_columns = [
    { field: "serialNo", headerName: "S. No." },
    { field: "fuelType", headerName: "Fuel Type" },
    { field: "fuelType", headerName: "Fuel Station" },
    { field: "meterReading", headerName: "Meter Reading" },
    { field: "fuelVolume", headerName: "Fuel Volume" },
  ];

  // Handle bar click & open sidebar
  const handleBarClick = (entry, idx) => {
    // const { rangeStart, rangeEnd } = entry;
    const rangeStart = idx == 0 ? idx : idx * transactionGap - 1;
    const rangeEnd = Math.min((idx + 1) * transactionGap, Array.isArray(fuelData) ? fuelData.length : 0);
    setSidebarData(fuelData.slice(rangeStart, rangeEnd));
    setRangeLabel(`${rangeStart + 1}-${rangeEnd}`); // 1-based index
    setShowSidebar(true);
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleEdit = () => {
    console.log("Edit clicked for row:");
    setEdit(true);
    setModalOpen(true);
    setDetails(false);
    setEditRowId(rowId);
  };

  const handleDetails = () => {
    console.log("Details clicked for row:");
    setModalOpen(true);
    setDetails(true);
    setEdit(false);
  };

  const handleModalClose = () => {
    console.log("handleModalClose called");
    setModalOpen(false);
    setEdit(false);
    setDetails(false);
  };

  const buttons = [];

  if (
    hasPermission(userPermissions, PERMISSIONS.VENDOR_FUEL_TRANSACTION_READ)
  ) {
    buttons.push({ label: "Details", onClick: () => handleDetails() });
  }

  if (
    hasPermission(userPermissions, PERMISSIONS.VENDOR_FUEL_TRANSACTION_UPDATE)
  ) {
    buttons.push({ label: "Edit", onClick: () => handleEdit() });
  }

  const columns = [
    { field: "sn", headerName: "S. No." },
    { field: "fuelType", headerName: "Fuel Type" },
    { field: "fuelStation", headerName: "Fuel Station" },
    { field: "driver", headerName: "Driver" },
    { field: "meterReading", headerName: "Meter Reading" },
    { field: "amount", headerName: "Amount" },
    { field: "fuelVolume", headerName: "Fuel Volume" },
  ];

  const view_columns = [
    { field: "driver_name", headerName: "Driver" },
    { field: "car_number", headerName: "Car Number" },
    { field: "amount", headerName: "Amount" },
    { field: "fuel_type", headerName: "Fuel Type" },
    {
      field: "fuel_amount",
      headerName: "Fuel Quantity (litres)",
      type: "number",
    },
    { field: "is_paid_by_driver", headerName: "Driver Paid" },
    { field: "description", headerName: "Description" },
    { field: "km_reading", headerName: "Km Reading" },
  ];

  const rows = (sidebarData || []).map((item) => ({
    id: item.fuelId,
    fuelType: item.fuelType,
    fuelStation: item.fuelStation || "-",
    driver: item.driverName,
    meterReading: item.meterReading,
    amount: item.fuelAmount,
    fuelVolume: item.fuelVolume,
  }));

  return (
    <div className="fuel-analysis-container">
      {/* Page Title */}
      <h1 className="fuel-analysis-pageTitle">Car Fuel Analysis</h1>

      <div className="fuel-analysis-carSelectWrapper fuel-analysis-carSelectFlex">
        <div className="fuel-analysis-carSelectCol">
          <label htmlFor="car-select" className="fuel-analysis-carSelectLabel">
            Car
          </label>
          <Select
            inputId="car-select"
            value={selectedCarInfo}
            onChange={setSelectedCarInfo}
            options={carList}
            getOptionLabel={(c) => c.vehicleNo}
            getOptionValue={(c) => c.id}
            placeholder="Choose a car…"
          />
        </div>
        <div className="fuel-analysis-carSelectCol">
          <label htmlFor="since-time" className="fuel-analysis-carSelectLabel">Since Time</label>
          <input
            id="since-time"
            type="datetime-local"
            className="fuel-analysis-dateInput"
            value={(() => {
              // Convert unix seconds to yyyy-MM-ddTHH:mm (local time)
              const d = new Date(sinceTime * 1000);
              const pad = (n) => n.toString().padStart(2, '0');
              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            })()}
            onChange={e => {
              const val = e.target.value;
              // Parse yyyy-MM-ddTHH:mm as local time
              const dt = new Date(val);
              setSinceTime(Math.floor(dt.getTime() / 1000));
            }}
          />
        </div>
      </div>

      {/* Info Card */}
      <div className="fuel-analysis-card">
        <div className="fuel-analysis-infoRow">
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Car Name</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.vehicleName}
            </p>
          </div>
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Assigned Driver</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.assignedDriverName}
            </p>
          </div>
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Capacity</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.capacity}
            </p>
          </div>
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Car Type</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.carTypeName}
            </p>
          </div>
        </div>
        <div className="fuel-analysis-infoRow">
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Model Name</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.modelName}
            </p>
          </div>
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Registration Date</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.registrationDate}
            </p>
          </div>
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Car Number</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.vehicleNo}
            </p>
          </div>
          <div className="fuel-analysis-infoBlock">
            <p className="fuel-analysis-infoLabel">Fuel Type</p>
            <p className="fuel-analysis-infoValue">
              {selectedCarInfo?.fuelType}
            </p>
          </div>
        </div>
      </div>

      {/* Analysis Charts */}
      <div className="fuel-analysis-analysisCharts">
        {/* Mileage Analysis Card */}
        <div className="fuel-analysis-chartCard">
          <div className="fuel-analysis-analysisRow">
            <h2 className="fuel-analysis-chartTitle">Mileage Analysis</h2>
            <div className="fuel-analysis-analysisControls">
              <label htmlFor="fuelType">Fuel Type</label>
              <select
                id="fuelType"
                value={selectedFuelType}
                onChange={handleFuelTypeChange}
              >
                {fuelTypes.map((type, idx) => (
                  <option key={idx} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="fuel-analysis-analysisControls">
              <label htmlFor="transactionGap">Transaction Gap</label>
              <select
                id="transactionGap"
                value={transactionGap}
                onChange={handleTransactionGapChange}
              >
                {transactionGapOptions.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Scrollable BarChart Container with onScroll */}
          <div
            className="fuel-analysis-scrollContainer"
            onScroll={handleScroll}
          >
            {barData.length === 0 ? (
              <div style={{padding: '2rem', textAlign: 'center', color: '#888'}}>No data available for the selected range.</div>
            ) : (
              <BarChart
                width={800} // Wider than container to enable horizontal scroll
                height={300}
                data={barData}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <XAxis
                  dataKey="name"
                  tickFormatter={(value) => `Latest ${value}`}
                />
                <YAxis />
                <Tooltip cursor={false} />
                <Bar dataKey="value">
                  {barData.map((entry, idx) => (
                    <Cell
                      key={`cell-${idx}`}
                      fill="#8884d8"
                      onClick={() => handleBarClick(entry, idx)}
                    />
                  ))}
                  <LabelList
                    dataKey="totalAmount"
                    position="inside"
                    formatter={(val) => `₹${val}`}
                    fill="#fff"
                  />
                </Bar>
              </BarChart>
            )}
            {loadingMore && (
              <div className="fuel-analysis-loadingIndicator">
                Loading more...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fuel Distribution (Pie Chart) Card */}
      <div className="fuel-analysis-chartCard">
        <div className="fuel-analysis-analysisRow">
          <h2 className="fuel-analysis-chartTitle">Fuel Distribution</h2>
          <div className="fuel-analysis-analysisControls">
            <label htmlFor="transactionGapPie">Transaction Gap</label>
            <select
              id="transactionGapPie"
              value={transactionGapPie}
              onChange={handleTransactionGapPieChange}
            >
              {transactionGapOptions.map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="fuel-analysis-donutContainer">
          {pieData.length === 0 ? (
            <div style={{padding: '2rem', textAlign: 'center', color: '#888'}}>No data available for the selected range.</div>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={300}
              key={transactionGapPie}
            >
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  label={(entry) => `₹${entry.value}`}
                  isAnimationActive={true}
                  animationDuration={500}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Sidebar detail panel */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: showSidebar ? "70%" : "0",
          height: "100%",
          background: "#fff",
          boxShadow: "-2px 0 5px rgba(0,0,0,0.3)",
          overflowY: "auto",
          zIndex: 1000,
          opacity: showSidebar ? 1 : 0,
          transition: "width 0.3s ease-in-out, opacity 0.3s ease-in-out",
          padding: showSidebar ? "1rem" : "0",
          visibility: showSidebar ? "visible" : "hidden"
        }}
      >
        {showSidebar && (
          <>
            <button
              onClick={() => setShowSidebar(false)}
              style={{ marginBottom: "1rem" }}
            >
              Close
            </button>
            <h3>Fuel Transactions Details</h3>
            {sidebarData.length ? (
              <div>
                <Table
                  table_columns={columns}
                  view_columns={columns}
                  rows={rows}
                  setRowsId={setRowId}
                  buttons={buttons}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  showPagination={false}
                />
                {isModalOpen && isDetails && (
                  <div className="details-container">
                    <Modal isOpen={isModalOpen} onClose={handleModalClose}>
                      <ViewEntity
                        id={rowId}
                        fetchFunction={ApiManager.getFuelTransaction}
                        columns={view_columns}
                        entity="id"
                      />
                    </Modal>
                  </div>
                )}
                {isModalOpen && isEdit && (
                  <div className="edit-container">
                    <Modal isOpen={isModalOpen} onClose={handleModalClose}>
                      <EditFuelTransaction
                        rowId={rowId}
                        toggleFunction={toggleModal}
                      />
                    </Modal>
                  </div>
                )}
              </div>
            ) : (
              <p>No details available.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FuelAnalysis;
